import { useState, useEffect, useRef } from "react";
import { ScoreFilter } from "../types";
import { useClickOutside } from "../../../hooks/useClickOutside";

const SLIDER_MIN = 0;
const SLIDER_MAX = 1;
const SLIDER_STEP = 0.01;

export interface UseScoreFilterProps {
  scoreFilter?: ScoreFilter;
  onScoreFilterChange?: (filter?: ScoreFilter) => void;
}

/**
 * Get initial slider values from score filter or defaults.
 */
function getSliderValues(scoreFilter?: ScoreFilter): [number, number] {
  if (scoreFilter?.min_score !== undefined && scoreFilter?.max_score !== undefined) {
    return [scoreFilter.min_score, scoreFilter.max_score];
  }
  if (scoreFilter?.min_score !== undefined) {
    return [scoreFilter.min_score, SLIDER_MAX];
  }
  if (scoreFilter?.max_score !== undefined) {
    return [SLIDER_MIN, scoreFilter.max_score];
  }
  return [SLIDER_MIN, SLIDER_MAX];
}

/**
 * Custom hook to manage score filter state and interactions.
 */
export function useScoreFilter({
  scoreFilter,
  onScoreFilterChange,
}: UseScoreFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [minScore, setMinScore] = useState<number>(SLIDER_MIN);
  const [maxScore, setMaxScore] = useState<number>(SLIDER_MAX);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize state from existing scoreFilter
  useEffect(() => {
    const [min, max] = getSliderValues(scoreFilter);
    setMinScore(min);
    setMaxScore(max);
  }, [scoreFilter]);

  // Close dropdown when clicking outside
  useClickOutside(containerRef, () => {
    if (isExpanded) {
      setIsExpanded(false);
      const [min, max] = getSliderValues(scoreFilter);
      setMinScore(min);
      setMaxScore(max);
    }
  }, isExpanded);

  const handleSliderChange = (values: number[]) => {
    if (values.length >= 2) {
      setMinScore(values[0]);
      setMaxScore(values[1]);
    }
  };

  const handleApply = () => {
    // Full range [0, 1] = no filter (deactivated)
    if (minScore === SLIDER_MIN && maxScore === SLIDER_MAX) {
      onScoreFilterChange?.(undefined);
    } else {
      onScoreFilterChange?.({
        min_score: minScore,
        max_score: maxScore,
      });
    }
    setIsExpanded(false);
  };

  const handleCancel = () => {
    setIsExpanded(false);
    const [min, max] = getSliderValues(scoreFilter);
    setMinScore(min);
    setMaxScore(max);
  };

  const handleRemove = () => {
    onScoreFilterChange?.(undefined);
    setMinScore(SLIDER_MIN);
    setMaxScore(SLIDER_MAX);
    setIsExpanded(false);
  };

  const isActive =
    !!scoreFilter &&
    (scoreFilter.min_score !== undefined || scoreFilter.max_score !== undefined);

  const canApply = minScore <= maxScore;

  return {
    isExpanded,
    setIsExpanded,
    minScore,
    maxScore,
    setMinScore,
    setMaxScore,
    handleSliderChange,
    handleApply,
    handleCancel,
    handleRemove,
    containerRef,
    isActive,
    canApply,
    sliderMin: SLIDER_MIN,
    sliderMax: SLIDER_MAX,
    sliderStep: SLIDER_STEP,
  };
}
